import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, ArrowRight, Check } from 'lucide-react';
import { COMPANY } from '../mock';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [website, setWebsite] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, website }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(typeof data.detail === 'string' ? data.detail : 'Please enter a valid email address.');
        return;
      }
      setDone(true);
      setEmail('');
      setTimeout(() => setDone(false), 5000);
    } catch {
      setError('We could not reach the server. Please try again shortly.');
    }
  };

  return (
    <footer className="bg-[#071627] text-[#f7f4ee]">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="container-lg py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow-light mb-3">Stay Informed</p>
            <h3 className="font-serif text-3xl md:text-4xl">Subscribe to hear from us.</h3>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-3" noValidate data-testid="newsletter-form">
            <div className="flex flex-col md:flex-row gap-3">
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input data-testid="newsletter-email-input"
              id="newsletter-email"
              name="email"
              autoComplete="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 min-w-0 w-full bg-transparent border border-white/25 px-5 py-3.5 text-[#f7f4ee] placeholder:text-white/40 focus:outline-none focus:border-[#c6a15b] transition-colors"
            />
            <button data-testid="newsletter-submit-button" type="submit" className="btn-gold justify-center shrink-0">
              {done ? (<><Check size={16} /> Subscribed</>) : (<>Sign Up <ArrowRight size={16} /></>)}
            </button>
            </div>
            <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true"><label htmlFor="newsletter-website">Website</label><input id="newsletter-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></div>
            {done && <p data-testid="newsletter-success" role="status" className="text-[#d9bd82] text-sm">Thank you. You are subscribed to LRG Holdings updates.</p>}
            {error && <p data-testid="newsletter-error" role="alert" className="text-red-300 text-sm">{error}</p>}
            <p className="text-white/40 text-xs font-light">We respect your inbox. Read our <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-[#c6a15b]">Privacy Policy</Link>.</p>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-lg py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <Globe className="text-[#c6a15b]" size={30} strokeWidth={1.2} />
            <span className="font-serif text-2xl tracking-[0.14em] font-semibold">LRG HOLDINGS</span>
          </div>
          <p className="text-white/60 max-w-md leading-relaxed font-light">
            A Botswana-based investment and development company connecting Botswana&rsquo;s industries and people to global opportunity across diamonds, leather, education and community development.
          </p>
        </div>
        <div>
          <p className="eyebrow-light mb-5">Explore</p>
          <ul className="space-y-3 text-white/70 text-sm">
            <li><Link to="/" className="hover:text-[#c6a15b] transition-colors">Home</Link></li>
            <li><Link to="/education" className="hover:text-[#c6a15b] transition-colors">Education</Link></li>
            <li><Link to="/diamonds" className="hover:text-[#c6a15b] transition-colors">Diamonds</Link></li>
            <li><Link to="/leather" className="hover:text-[#c6a15b] transition-colors">Leather</Link></li>
            <li><Link to="/community" className="hover:text-[#c6a15b] transition-colors">Community</Link></li>
            <li><Link to="/contact" className="hover:text-[#c6a15b] transition-colors">Contact</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow-light mb-5">Contact</p>
          <ul className="space-y-3 text-white/70 text-sm">
            <li>{COMPANY.location}</li>
            <li><a href={`mailto:${COMPANY.email}`} className="hover:text-[#c6a15b] transition-colors break-all">{COMPANY.email}</a></li>
            <li className="text-white/50">Founder &middot; {COMPANY.founder}</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-lg py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-white/40 text-xs tracking-wider">
          <span>Copyright &copy; {new Date().getFullYear()} LRG Holdings. All Rights Reserved.</span>
          <nav className="flex flex-wrap gap-x-5 gap-y-2" aria-label="Legal"><Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[#c6a15b] transition-colors">Privacy Policy</Link><Link to="/terms-of-use" data-testid="footer-terms-link" className="hover:text-[#c6a15b] transition-colors">Terms of Use</Link><Link to="/cookie-policy" data-testid="footer-cookies-link" className="hover:text-[#c6a15b] transition-colors">Cookie Policy</Link></nav>
          <span className="uppercase tracking-[0.2em]">Gaborone &middot; Baltimore &middot; Chicago</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
